import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AuditPage from './AuditPage';
import DashboardPage from './DashboardPage';
import SymbolsPage from './SymbolsPage';
import StrategiesPage from './StrategiesPage';
import BacktestPage from './BacktestPage';
import StrategyBuilderPage from './StrategyBuilderPage';
import StrategyAnalyticsLayout from './analytics/StrategyAnalyticsLayout';
import OptimizationsLayout from './optimizations/OptimizationsLayout';
import QuantLayout from './quant/QuantLayout';
import PortfolioV2 from './portfolio/PortfolioV2';
import PerformancePage from './PerformancePage';
import TwrPerformancePage from './TwrPerformancePage';
import RiskPage from './RiskPage';
import TwrComparisonPage from './TwrComparisonPage';
import BasketsPage from './BasketsPage';
import OrdersPage from './OrdersPage';
import IBKRAccountsPage from './IBKRAccountsPage';
import ExternalBookPage from './ExternalBookPage';
import DeploymentsPage from './DeploymentsPage';
import InfusionsPage from './InfusionsPage';
import SystemPage from './system/SystemPage';
import PriceIntegrityPage from './PriceIntegrityPage';
import SecurityLifecyclePage from './SecurityLifecyclePage';
import SectorRotationPage from './SectorRotationPage';

// Four pillars. Each groups existing trading tabs as sub-tabs — this is purely a
// re-nesting of the navigation; every page still mounts unchanged below by path.
// `extraPaths` are additional URLs that belong to the pillar for active-highlight
// (kept reachable, just not shown as their own sub-tab).
const PILLARS = [
  {
    id: 'portfolio', label: 'Portfolio',
    subs: [
      { path: '/trading/portfolio', label: 'Overview' },
      { path: '/trading/twr-perf', label: 'Performance' },
      { path: '/trading/risk', label: 'Risk' },
      { path: '/trading/infusions', label: 'Infusion Manager' },
      { path: '/trading/orders', label: 'Orders' },
    ],
    extraPaths: ['/trading/performance', '/trading/twr-compare'],
  },
  {
    id: 'strategies', label: 'Strategies',
    subs: [
      { path: '/trading', label: 'Strategy Builder', end: true },
      { path: '/trading/strategies', label: 'Strategies' },
      { path: '/trading/deployments', label: 'Deployments' },
      { path: '/trading/baskets', label: 'Baskets' },
      { path: '/trading/optimizations', label: 'Optimizations' },
      { path: '/trading/sector-rotation', label: 'Sector Rotation' },
      { path: '/trading/analytics', label: 'Analytics' },
      { path: '/trading/quant', label: 'Quant' },
    ],
  },
  {
    id: 'systems', label: 'Systems',
    subs: [
      { path: '/trading/system', label: 'System' },
      { path: '/trading/accounts', label: 'IBKR Accounts' },
      { path: '/trading/external-book', label: 'External Book' },
    ],
  },
  {
    id: 'data', label: 'Data',
    subs: [
      { path: '/trading/symbols', label: 'Symbols' },
      { path: '/trading/price-integrity', label: 'Price Integrity' },
      { path: '/trading/security-lifecycle', label: 'Security Lifecycle' },
      { path: '/trading/dashboard', label: 'Dashboard' },
    ],
    extraPaths: ['/trading/backtest'],
  },
];

// A delegated OPERATOR (e.g. Joji on the Alternatives Portfolio) sees a curated subset — the
// account is scoped server-side, so every tab here is already limited to their delegated
// account. Investing/creation tools (Strategy Builder, Optimizations, Quant, Sector Rotation)
// and platform admin (System, IBKR Accounts, Symbols, Price Integrity, Dashboard) are omitted:
// an operator views + runs External-Book mechanics, never makes an investing decision. The
// backend still 403s any write they can't do — this is UX curation on top of that enforcement.
const OPERATOR_PILLARS = [
  {
    id: 'portfolio', label: 'Portfolio',
    subs: [
      { path: '/trading/portfolio', label: 'Overview' },
      { path: '/trading/twr-perf', label: 'Performance' },
      { path: '/trading/risk', label: 'Risk' },
      { path: '/trading/orders', label: 'Orders' },
    ],
    extraPaths: ['/trading/performance'],
  },
  {
    id: 'book', label: 'External Book',
    subs: [
      { path: '/trading/external-book', label: 'Order Book' },
      { path: '/trading/infusions', label: 'Infusions' },
      { path: '/trading/deployments', label: 'Deployments' },
    ],
  },
  {
    id: 'audit', label: 'Audit',
    subs: [
      { path: '/trading/audit', label: 'Audit Trail' },
    ],
  },
];

const pathMatch = (p, cur) => (p === '/trading' ? cur === '/trading' : cur.startsWith(p));

const TradingLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentPath = location.pathname.replace(/\/$/, '') || '/trading';

  // Operator (delegate, non-admin) → curated pillar set + landing on the External Book.
  const isOperator = !!(user?.trading_delegate?.is_delegate) && !user?.is_admin;
  const pillars = isOperator ? OPERATOR_PILLARS : PILLARS;

  const operatorAllowed = useMemo(
    () => OPERATOR_PILLARS.flatMap((pl) => [...pl.subs.map((s) => s.path), ...(pl.extraPaths || [])]),
    [],
  );
  // Bounce an operator off any tab outside their curated set (e.g. a hand-typed URL or the
  // default '/trading' Strategy Builder) to their Order Book.
  useEffect(() => {
    if (!isOperator) return;
    if (!operatorAllowed.some((p) => pathMatch(p, currentPath))) {
      navigate('/trading/external-book', { replace: true });
    }
  }, [isOperator, currentPath, navigate, operatorAllowed]);

  const activePillar = pillars.find((pl) =>
    [...pl.subs.map((s) => s.path), ...(pl.extraPaths || [])].some((p) => pathMatch(p, currentPath)),
  ) || pillars[0];
  const activeSub = activePillar.subs.find((s) => pathMatch(s.path, currentPath)) || activePillar.subs[0];

  // Track which tabs have been visited — mount on first visit, keep alive after.
  const [mounted, setMounted] = useState(() => ({ '/trading': true, [currentPath]: true }));
  const go = (path) => { setMounted((prev) => ({ ...prev, [path]: true })); navigate(path); };

  return (
    <div>
      {/* breadcrumb back to the two-platform landing */}
      <div className="flex items-center gap-2 mb-3 text-xs">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-brand-gold transition-colors"
        >
          <span className="text-base leading-none">&lsaquo;</span> Platforms
        </button>
        <span className="text-gray-300 dark:text-gray-600">/</span>
        <span className="text-gray-600 dark:text-gray-300 font-medium">Trading Engine</span>
      </div>

      {/* pillar tabs — segmented control */}
      <nav className="inline-flex flex-wrap gap-1 p-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#0d1117]">
        {pillars.map((pl) => (
          <button
            key={pl.id}
            onClick={() => go(pl.subs[0].path)}
            className={`px-4 py-1.5 text-sm rounded-lg whitespace-nowrap transition-colors border ${
              pl === activePillar
                ? 'bg-brand-gold/10 text-brand-gold font-semibold border-brand-gold/40'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {pl.label}
          </button>
        ))}
      </nav>

      {/* sub-tabs of the active pillar */}
      <div className="flex overflow-x-auto gap-5 mt-3 mb-4 scrollbar-thin">
        {activePillar.subs.map((s) => (
          <button
            key={s.path}
            onClick={() => go(s.path)}
            className={`pb-1.5 text-[13px] whitespace-nowrap border-b-2 transition-colors ${
              s === activeSub
                ? 'border-brand-gold text-gray-900 dark:text-gray-100 font-medium'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Render pages with display:none instead of unmounting — preserves state */}
      <div style={{ display: currentPath === '/trading' ? 'block' : 'none' }}>
        {mounted['/trading'] && !isOperator && <StrategyBuilderPage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/portfolio') ? 'block' : 'none' }}>
        {mounted['/trading/portfolio'] && <PortfolioV2 />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/orders') ? 'block' : 'none' }}>
        {mounted['/trading/orders'] && <OrdersPage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/performance') ? 'block' : 'none' }}>
        {mounted['/trading/performance'] && <PerformancePage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/twr-perf') ? 'block' : 'none' }}>
        {mounted['/trading/twr-perf'] && <TwrPerformancePage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/risk') ? 'block' : 'none' }}>
        {mounted['/trading/risk'] && <RiskPage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/infusions') ? 'block' : 'none' }}>
        {mounted['/trading/infusions'] && <InfusionsPage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/twr-compare') ? 'block' : 'none' }}>
        {mounted['/trading/twr-compare'] && <TwrComparisonPage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/analytics') ? 'block' : 'none' }}>
        {mounted['/trading/analytics'] && <StrategyAnalyticsLayout />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/optimizations') ? 'block' : 'none' }}>
        {mounted['/trading/optimizations'] && <OptimizationsLayout />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/sector-rotation') ? 'block' : 'none' }}>
        {mounted['/trading/sector-rotation'] && <SectorRotationPage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/quant') ? 'block' : 'none' }}>
        {mounted['/trading/quant'] && <QuantLayout />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/backtest') ? 'block' : 'none' }}>
        {mounted['/trading/backtest'] && <BacktestPage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/strategies') ? 'block' : 'none' }}>
        {mounted['/trading/strategies'] && <StrategiesPage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/deployments') ? 'block' : 'none' }}>
        {mounted['/trading/deployments'] && <DeploymentsPage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/baskets') ? 'block' : 'none' }}>
        {mounted['/trading/baskets'] && <BasketsPage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/symbols') ? 'block' : 'none' }}>
        {mounted['/trading/symbols'] && <SymbolsPage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/dashboard') ? 'block' : 'none' }}>
        {mounted['/trading/dashboard'] && <DashboardPage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/accounts') ? 'block' : 'none' }}>
        {mounted['/trading/accounts'] && <IBKRAccountsPage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/audit') ? 'block' : 'none' }}>
        {mounted['/trading/audit'] && <AuditPage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/external-book') ? 'block' : 'none' }}>
        {mounted['/trading/external-book'] && <ExternalBookPage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/price-integrity') ? 'block' : 'none' }}>
        {mounted['/trading/price-integrity'] && <PriceIntegrityPage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/security-lifecycle') ? 'block' : 'none' }}>
        {mounted['/trading/security-lifecycle'] && <SecurityLifecyclePage />}
      </div>
      <div style={{ display: currentPath.startsWith('/trading/system') ? 'block' : 'none' }}>
        {mounted['/trading/system'] && <SystemPage />}
      </div>
    </div>
  );
};

export default TradingLayout;
